import { environment } from '@env/environment';

export type SseEvent = Record<string, unknown> & { type?: string; message?: string };

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    authfor: 'W',
    'Content-Type': 'application/json',
  };
}

async function readSseStream(body: ReadableStream<Uint8Array>, onEvent: (ev: SseEvent) => void): Promise<void> {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += dec.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of block.split('\n')) {
        if (line.startsWith('data:')) {
          const raw = line.slice(5).trim();
          if (!raw) {
            continue;
          }
          try {
            onEvent(JSON.parse(raw) as SseEvent);
          } catch {
            onEvent({ type: 'progress', message: raw });
          }
        }
      }
    }
  }
}

export type PostSseOptions = {
  /**
   * Abort the request after this many ms (so the UI can recover without a full reload).
   * Use `0` to disable (wait as long as the server needs — e.g. slide AI + image generation).
   */
  timeoutMs?: number;
  signal?: AbortSignal;
};

/** POST JSON { criteria } and consume `text/event-stream` responses (our backend sends JSON in each `data:` line). */
export async function postSse(
  apiPath: string,
  criteria: Record<string, unknown>,
  onEvent: (ev: SseEvent) => void,
  options?: PostSseOptions
): Promise<void> {
  const url = `${environment.baseUrl}${apiPath.startsWith('/') ? '' : '/'}${apiPath}`;
  const payload = {
    criteria: {
      ...criteria,
      tenant: criteria['tenant'] ?? null,
    },
  };

  const rawTimeout =
    options?.timeoutMs !== undefined && options?.timeoutMs !== null
      ? Number(options.timeoutMs)
      : 240_000;
  const timeoutMs = Number.isFinite(rawTimeout) ? rawTimeout : 240_000;

  const ctrl = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (timeoutMs > 0) {
    timeoutId = setTimeout(() => ctrl.abort(), timeoutMs);
  }
  const parent = options?.signal;
  const onParentAbort = () => ctrl.abort();
  if (parent) {
    if (parent.aborted) ctrl.abort();
    else parent.addEventListener('abort', onParentAbort, { once: true });
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      let detail = res.statusText || `HTTP ${res.status}`;
      try {
        const t = await res.text();
        if (t) {
          const trimmed = t.trim();
          if (trimmed.startsWith('{')) {
            try {
              const o = JSON.parse(trimmed) as Record<string, unknown>;
              const data = (o['Data'] ?? o['data']) as Record<string, unknown> | undefined;
              const nested =
                data && typeof data === 'object'
                  ? String(data['message'] ?? data['error'] ?? '').trim()
                  : '';
              const top = String(
                o['message'] ?? o['errorDescription'] ?? (typeof o['error'] === 'string' ? o['error'] : '') ?? '',
              ).trim();
              detail = nested || top || trimmed;
            } catch {
              detail = trimmed;
            }
          } else {
            detail = trimmed;
          }
        }
      } catch {
        /* ignore */
      }
      throw new Error(
        detail ||
          `Server error (${res.status}). Processing may not have completed — try again.`,
      );
    }
    if (!res.body) {
      throw new Error('Empty response body');
    }
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.includes('text/event-stream')) {
      const t = await new Response(res.body).text();
      throw new Error(t || 'Expected text/event-stream');
    }
    await readSseStream(res.body, onEvent);
  } catch (e: unknown) {
    const err = e as { name?: string; message?: string };
    if (err?.name === 'AbortError') {
      const timedOut = timeoutMs > 0;
      throw new Error(
        timedOut
          ? `The request timed out after ${Math.round(timeoutMs / 1000)}s before processing finished. The AI service may still be working or unavailable — try again; no need to restart the app.`
          : 'The request was cancelled before processing finished. You can try again; no need to restart the app.',
      );
    }
    if (e instanceof Error) throw e;
    throw new Error(String(e));
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (parent) parent.removeEventListener('abort', onParentAbort);
  }
}

export async function downloadWithAuth(path: string): Promise<Blob> {
  const url = `${environment.baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    throw new Error(res.statusText || `HTTP ${res.status}`);
  }
  return res.blob();
}
